import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Layout from '../../components/layout/Layout';
import CustomButton from '../../components/CustomButton';
import TextField from '../../components/TextField';
import HTMLEditor from '../../components/HTMLEditor';
import { getPremiumSettings, updateAllPremiumSettings } from '../../redux/actions/premiumSettings';
import PremiumTool from './PremiumTool';
import { apiUrl } from '../../config/settings';

const PremiumSettings = () => {
  const dispatch = useDispatch();
  const configs = useSelector((state) => state.premiumSettings.configs);
  const [currentEditingCapability, setCurrentEditingCapability] = useState(null);
  const [premiumKnowledgeHtmlContent, setPremiumKnowledgeHtmlContent] = useState('');

  // Capability HTML states
  const [capabilityConfigs, setCapabilityConfigs] = useState({
    teladoc: { html: '', title: '', description: '' },
    baby_first_aid: { html: '', title: '', description: '' },
    prenatal_exercises: { html: '', title: '', description: '' },
    name_finder: { html: '', title: '', description: '' },
    newborn_care: { html: '', title: '', description: '' },
    guided_meditation: { html: '', title: '', description: '' },
  });
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [capabilitySaving, setCapabilitySaving] = useState({});

  useEffect(() => {
    dispatch(getPremiumSettings());
    loadCapabilityConfigs();
  }, [dispatch]);

  // Load capability HTML configs from backend
  const loadCapabilityConfigs = async () => {
    setCapabilityLoading(true);
    try {
      const response = await fetch(`${apiUrl}/config/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const allConfigs = await response.json();
        const capabilities = [
          'teladoc',
          'baby_first_aid',
          'prenatal_exercises',
          'name_finder',
          'newborn_care',
          'guided_meditation',
        ];
        const newCapabilityConfigs = {};

        capabilities.forEach((capability) => {
          const configKey = `capability_${capability}_html`;
          const config = allConfigs.find((c) => c.key === configKey);

          newCapabilityConfigs[capability] = {
            html: config && config.data && config.data.html ? config.data.html : '',
            title: config && config.data && config.data.title ? config.data.title : '',
            description:
              config && config.data && config.data.description ? config.data.description : '',
          };
        });

        setCapabilityConfigs(newCapabilityConfigs);
      }
    } catch (error) {
      console.error('Error loading capability configs:', error);
    } finally {
      setCapabilityLoading(false);
    }
  };

  useEffect(() => {
    if (configs && Array.isArray(configs)) {
      // Load premium knowledge content
      const knowledgeConfig = configs?.find((item) => item.key === 'premiumKnowledgeDescription');
      setPremiumKnowledgeHtmlContent(knowledgeConfig?.data || '');
    }
  }, [configs]);

  const save = () => {
    const settings = {
      premiumKnowledgeDescription: premiumKnowledgeHtmlContent || '',
    };

    dispatch(updateAllPremiumSettings(settings));
  };

  const editCapabilityItem = (capability) => {
    setCurrentEditingCapability(capability);
  };

  const saveCurrentCapability = () => {
    if (currentEditingCapability) {
      saveCapabilityConfig(currentEditingCapability);
      setCurrentEditingCapability(null);
    }
  };

  // Save individual capability HTML config
  const saveCapabilityConfig = async (capability) => {
    setCapabilitySaving((prev) => ({ ...prev, [capability]: true }));
    try {
      const configKey = `capability_${capability}_html`;
      const config = capabilityConfigs[capability];

      const response = await fetch(`${apiUrl}/config/${configKey}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            html: config.html,
            title: config.title,
            description: config.description,
            updatedAt: new Date(),
          },
        }),
      });

      if (response.ok) {
        alert(`${capability} configuration saved successfully!`);
      } else {
        alert(`Failed to save ${capability} configuration`);
      }
    } catch (error) {
      console.error(`Error saving ${capability} config:`, error);
      alert(`Error saving ${capability} configuration`);
    } finally {
      setCapabilitySaving((prev) => ({ ...prev, [capability]: false }));
    }
  };

  // Update capability config state
  const updateCapabilityConfig = (capability, field, value) => {
    setCapabilityConfigs((prev) => ({
      ...prev,
      [capability]: {
        ...prev[capability],
        [field]: value,
      },
    }));
  };

  // Get display name for capability
  const getCapabilityDisplayName = (capability) => {
    const names = {
      teladoc: 'Teladoc Medical Consultation',
      baby_first_aid: 'Baby First Aid Course',
      prenatal_exercises: 'Prenatal Exercise Program',
      name_finder: 'Baby Name Finder Tool',
      newborn_care: 'Newborn Care Course',
      guided_meditation: 'Guided Meditation',
    };
    return names[capability] || capability;
  };

  const renderCapabilityEditor = () => {
    if (currentEditingCapability) {
      const capability = currentEditingCapability;
      return (
        <div className="bg-white p-6 rounded shadow mt-4">
          <div className="font-medium text-lg mb-4 text-center">
            Edit {getCapabilityDisplayName(capability)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <TextField
              type="text"
              label="Title"
              value={capabilityConfigs[capability].title}
              onChange={(e) => updateCapabilityConfig(capability, 'title', e.target.value)}
            />
            <TextField
              type="text"
              label="Description"
              value={capabilityConfigs[capability].description}
              onChange={(e) => updateCapabilityConfig(capability, 'description', e.target.value)}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">HTML Content</label>
            <div className="border border-gray-300 rounded">
              <HTMLEditor
                title={`${getCapabilityDisplayName(capability)} HTML Content`}
                defaultValue={capabilityConfigs[capability].html}
                setContents={capabilityConfigs[capability].html}
                onChange={(content) => updateCapabilityConfig(capability, 'html', content)}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <CustomButton onClick={() => setCurrentEditingCapability(null)}>Cancel</CustomButton>
            <CustomButton onClick={saveCurrentCapability} disabled={capabilitySaving[capability]}>
              {capabilitySaving[capability] ? 'Saving...' : 'Save'}
            </CustomButton>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout active="premium-settings">
      <div className="mx-24">
        <div className="font-semibold text-lg text-center mt-10">Premium settings deeplink</div>
        <TextField
          type="text"
          label="Deeplink"
          name="deeplink"
          value="preghello://home/subscription"
          disabled
        />
        <div className="font-semibold text-lg text-center mt-10">Video course settings</div>
        <PremiumTool />
        <div className="font-semibold text-lg text-center mt-10">Video courses description</div>
        <div className="bg-gray-50 p-4 rounded">
          <HTMLEditor
            title="Content (html)"
            defaultValue={premiumKnowledgeHtmlContent}
            setContents={premiumKnowledgeHtmlContent}
            onChange={(content) => {
              setPremiumKnowledgeHtmlContent(content);
            }}
          />
        </div>
        <div className="font-semibold text-lg text-center mt-10">Dropdown items</div>
        <div className="bg-gray-50 p-4 rounded">
          {capabilityLoading ? (
            <div className="text-center py-8">Loading capability configurations...</div>
          ) : (
            Object.keys(capabilityConfigs).map((capability) => (
              <div
                key={capability}
                className="flex items-center justify-between bg-white p-4 rounded mb-2"
              >
                <div className="flex-grow">
                  <div className="font-medium">{getCapabilityDisplayName(capability)}</div>
                  <div className="text-gray-600">
                    Title: {capabilityConfigs[capability].title || 'No title set'}
                  </div>
                  <div className="text-gray-600">
                    Description: {capabilityConfigs[capability].description || 'No description set'}
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <CustomButton onClick={() => editCapabilityItem(capability)}>Edit</CustomButton>
                </div>
              </div>
            ))
          )}
          {renderCapabilityEditor()}
        </div>

        <div className="flex justify-center my-5">
          <CustomButton onClick={save}>Save Premium Settings</CustomButton>
        </div>
      </div>
    </Layout>
  );
};

export default PremiumSettings;
